import {
  fetchPageAssets,
  pageAssetExists,
  pageLocaleVariantAvailability,
} from '../ducks/hostedAssets.duck';
import { normalizeLocale, resolveLocale } from '../util/genericHelpers';

const LEGAL_PAGE_TITLE_MESSAGE_IDS = {
  'terms-of-service': 'LegalPage.termsOfServiceTitle',
  'privacy-policy': 'LegalPage.privacyPolicyTitle',
};

export const getLegalPageTitleMessageId = assetName =>
  LEGAL_PAGE_TITLE_MESSAGE_IDS[assetName] || null;

export const localizeLegalPageTitle = (pageData, assetName, intl) => {
  const messageId = getLegalPageTitleMessageId(assetName);
  if (!pageData || !messageId || !intl) {
    return pageData;
  }

  const localizedTitle = intl.formatMessage({ id: messageId });
  const sections = Array.isArray(pageData.sections)
    ? pageData.sections.map(section => {
        const title = section?.title;
        return title?.fieldType === 'heading1'
          ? { ...section, title: { ...title, content: localizedTitle } }
          : section;
      })
    : pageData.sections;
  const pageTitle = pageData.meta?.pageTitle;
  const meta = pageTitle
    ? {
        ...pageData.meta,
        pageTitle: {
          ...pageTitle,
          content: localizedTitle,
        },
      }
    : pageData.meta;

  return { ...pageData, sections, meta };
};

export const getLegalPageAssetPath = (assetName, locale) => {
  const localeSuffix = normalizeLocale(locale) === 'th' ? '-thai' : '';
  return `content/pages/${assetName}${localeSuffix}.json`;
};

export const isLegalPageAssetForLocale = (asset, assetName, locale) => {
  const currentPath = String(asset?.path || '').replace(/^\//, '');
  return currentPath === getLegalPageAssetPath(assetName, locale);
};

/**
 * Create a route data loader for a localized legal document.
 *
 * Both public legal pages use the same locale priority as IntlFromUser:
 * URL > localStorage > user profile > hosted config > browser detection.
 */
export const createLegalPageLoadData = ({ assetName, stateKey }) => {
  return (params, search, config) => (dispatch, getState) => {
    const { currentUser } = getState().user || {};
    const locationPathname =
      params.locale === 'th'
        ? '/th'
        : typeof window !== 'undefined'
        ? window.location?.pathname
        : undefined;
    const locale = resolveLocale({
      currentUser,
      config,
      locationSearch: search,
      locationPathname,
    });
    const englishPath = getLegalPageAssetPath(assetName, 'en');
    const thaiPath = getLegalPageAssetPath(assetName, 'th');

    if (normalizeLocale(locale) === 'th') {
      return Promise.resolve(dispatch(fetchPageAssets({ [stateKey]: thaiPath }, true))).then(
        localizedAssets => {
          const hasThaiVariant = Boolean(localizedAssets?.[stateKey]);
          dispatch(pageLocaleVariantAvailability(assetName, hasThaiVariant));
          return hasThaiVariant
            ? localizedAssets
            : dispatch(fetchPageAssets({ [stateKey]: englishPath }, true));
        }
      );
    }

    return Promise.all([
      dispatch(fetchPageAssets({ [stateKey]: englishPath }, true)),
      dispatch(pageAssetExists(thaiPath)),
    ]).then(([englishAssets, hasThaiVariant]) => {
      dispatch(pageLocaleVariantAvailability(assetName, hasThaiVariant));
      return englishAssets;
    });
  };
};
